import { z } from 'zod';
import { API_CONFIG, apiUrl } from '../config/api';
import { apiClient } from '../lib/apiClient';
import { apiResponseSchema } from '../lib/schemas';

async function downloadExcel(url: string, defaultFilename: string): Promise<void> {
  const fullUrl = url.startsWith('http') ? url : `${API_CONFIG.baseURL}${url}`;
  const response = await fetch(fullUrl, { credentials: 'include' });
  if (!response.ok) {
    const text = await response.text();
    let message = response.statusText;
    try {
      const json = JSON.parse(text);
      if (json?.error) message = json.error;
    } catch {
      if (text) message = text;
    }
    throw new Error(message);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  let filename = defaultFilename;
  if (disposition) {
    const match = /filename="?([^";\n]+)"?/.exec(disposition);
    if (match?.[1]) filename = match[1].trim();
  }
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(objectUrl);
}

const groupReportEmployeeSchema = z.object({
  employee_id: z.string(),
  employee_name: z.string(),
  billable_hours: z.number(),
  travel_time_billable: z.number(),
  worktype: z.string(),
  sales_rate: z.number(),
  turnover: z.number(),
  is_excluded: z.boolean(),
});

const groupReportTotalsSchema = z.object({
  total_billable_hours: z.number(),
  total_travel_time_billable: z.number(),
  total_turnover: z.number(),
});

const groupReportResponseSchema = z.object({
  employees: z.array(groupReportEmployeeSchema),
  totals: groupReportTotalsSchema,
  include_travel_time: z.boolean(),
});

const isolatedReportEmployeeItemSchema = z.object({
  employee_id: z.string(),
  employee_name: z.string(),
});

const isolatedReportEmployeeListResponseSchema = z.object({
  employees: z.array(isolatedReportEmployeeItemSchema),
});

const isolatedReportEntrySchema = z.object({
  date: z.string(),
  day: z.string(),
  task: z.string(),
  description: z.string(),
  hours: z.number(),
  travel_time: z.number(),
  sales_rate: z.number(),
});

const isolatedReportSummarySchema = z.object({
  billable_hours: z.number(),
  travel_time_hours: z.number(),
  worktype: z.string(),
  sales_rate: z.number(),
  turnover: z.number(),
});

const isolatedReportResponseSchema = z.object({
  employee_id: z.string(),
  employee_name: z.string(),
  entries: z.array(isolatedReportEntrySchema),
  summary: z.array(isolatedReportSummarySchema).optional(),
  include_travel_time: z.boolean(),
  show_isolated_summary: z.boolean(),
});

export type GroupReportEmployee = z.infer<typeof groupReportEmployeeSchema>;
export type GroupReportTotals = z.infer<typeof groupReportTotalsSchema>;
export type GroupReportResponse = z.infer<typeof groupReportResponseSchema>;
export type IsolatedReportEmployeeItem = z.infer<typeof isolatedReportEmployeeItemSchema>;
export type IsolatedReportEmployeeListResponse = z.infer<typeof isolatedReportEmployeeListResponseSchema>;
export type IsolatedReportEntry = z.infer<typeof isolatedReportEntrySchema>;
export type IsolatedReportSummary = z.infer<typeof isolatedReportSummarySchema>;
export type IsolatedReportResponse = z.infer<typeof isolatedReportResponseSchema>;

export const customerReportService = {
  async getGroupReport(projectNumber: string, month: string, order?: string): Promise<GroupReportResponse> {
    let url = `${apiUrl.customerReports.group()}?project_number=${encodeURIComponent(projectNumber)}&month=${encodeURIComponent(month)}`;
    if (order && order !== '') {
      url += `&order=${encodeURIComponent(order)}`;
    }
    const result = await apiClient.get(url, {
      validate: apiResponseSchema(groupReportResponseSchema),
      deduplicate: true,
      context: 'getGroupReport',
    });
    return result.data;
  },

  async getIsolatedReportEmployees(projectNumber: string, month: string, order?: string): Promise<IsolatedReportEmployeeListResponse> {
    let url = `${apiUrl.customerReports.isolated()}?project_number=${encodeURIComponent(projectNumber)}&month=${encodeURIComponent(month)}`;
    if (order && order !== '') {
      url += `&order=${encodeURIComponent(order)}`;
    }
    const result = await apiClient.get(url, {
      validate: apiResponseSchema(isolatedReportEmployeeListResponseSchema),
      deduplicate: true,
      context: 'getIsolatedReportEmployees',
    });
    return result.data;
  },

  async getIsolatedReportByEmployee(
    projectNumber: string,
    month: string,
    employeeId: string,
    order?: string
  ): Promise<IsolatedReportResponse> {
    let url = `${apiUrl.customerReports.isolated(employeeId)}?project_number=${encodeURIComponent(projectNumber)}&month=${encodeURIComponent(month)}`;
    if (order && order !== '') {
      url += `&order=${encodeURIComponent(order)}`;
    }
    const result = await apiClient.get(url, {
      validate: apiResponseSchema(isolatedReportResponseSchema),
      deduplicate: true,
      context: 'getIsolatedReportByEmployee',
    });
    return result.data;
  },

  async exportGroupReportExcel(projectNumber: string, month: string, order?: string): Promise<void> {
    let url = `${apiUrl.customerReports.groupExportExcel()}?project_number=${encodeURIComponent(projectNumber)}&month=${encodeURIComponent(month)}`;
    if (order && order !== '') {
      url += `&order=${encodeURIComponent(order)}`;
    }
    await downloadExcel(url, `group-report-${projectNumber}-${month}.xlsx`);
  },

  async exportIsolatedReportExcel(
    projectNumber: string,
    month: string,
    employeeId: string,
    order?: string
  ): Promise<void> {
    let url = `${apiUrl.customerReports.isolatedExportExcel(employeeId)}?project_number=${encodeURIComponent(projectNumber)}&month=${encodeURIComponent(month)}`;
    if (order && order !== '') {
      url += `&order=${encodeURIComponent(order)}`;
    }
    await downloadExcel(url, `isolated-report-${projectNumber}-${month}-${employeeId}.xlsx`);
  },
};
